<template>
  <a-input
    :value="modelValue"
    :maxlength="maxLength"
    :placeholder="placeholder"
    :style="{ height: `${height}px !important` }"
    @update:value="(val: string) => emit('update:modelValue', val)"
    @paste="handlePaste"
  >
    <template #suffix>
      <div
        v-if="timeCount > 0"
        class="code-countdown"
      >
        {{ buttonText }}
      </div>
      <a-button
        v-else
        :disabled="isDisabled"
        class="neutral"
        :style="{
          // marginRight: '-12px'
        }"
        type="primary"
        @click="handleSendCode"
      >
        {{ buttonText }}
      </a-button>
    </template>
  </a-input>
</template>

<script setup lang="ts">
import type { Rule } from 'ant-design-vue/es/form'

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

interface Props {
  /** 表单项的字段名 */
  name: string | string[]
  /** 验证码类型：'sms' | 'email' */
  type?: 'sms' | 'email'
  /** 验证规则 */
  rules?: Rule[]
  /** 最大长度 */
  maxLength?: number
  /** 占位符文本 */
  placeholder?: string
  /** 是否禁用发送按钮 */
  disabled?: boolean
  /** 发送前的校验函数，返回 true 才能发送 */
  beforeSend?: () => boolean | Promise<boolean>
  /** v-model 绑定值 */
  modelValue: string
  /** 倒计时时长（秒） */
  countDown?: number
  /** 是否自动发送验证码 */
  autoSend?: boolean
  /** 输入框高度 */
  height?: number
  /** 国际化文本 */
  i18nText?: {
    获取验证码: string
    获取邮箱验证码: string
    后可重新获取: string
  }
}

const props = withDefaults(defineProps<Props>(), {
  type: 'sms',
  maxLength: 6,
  placeholder: '请输入验证码',
  disabled: false,
  countDown: 60,
  modelValue: '',
  autoSend: true,
  height: 50,
  i18nText: () => ({
    获取验证码: '获取验证码',
    获取邮箱验证码: '获取邮箱验证码',
    后可重新获取: '后可重新获取',
  }),
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'send'): void
}>()

// 倒计时
const timeCount = ref(0)
let timer: number

// 是否禁用发送按钮
const isDisabled = computed(() => props.disabled || timeCount.value > 0)

// 按钮文本
const buttonText = computed(() => {
  if (timeCount.value > 0) {
    return `${timeCount.value}s${props.i18nText['后可重新获取']}`
  }
  return props.type === 'sms' ? props.i18nText['获取验证码'] : props.i18nText['获取邮箱验证码']
})

// 开始倒计时
const startCountDown = () => {
  timeCount.value = props.countDown
  timer = window.setInterval(() => {
    timeCount.value--
    if (timeCount.value <= 0) {
      clearInterval(timer)
    }
  }, 1000)
}

// 处理发送验证码
const handleSendCode = async () => {
  if (isDisabled.value) return

  // 发送前校验
  if (props.beforeSend) {
    const canSend = await props.beforeSend()
    if (!canSend) return
  }

  // 触发发送事件
  emit('send')
  // 开始倒计时
  // startCountDown()
}

const handlePaste = (e: ClipboardEvent) => {
  e.preventDefault()
  const paste = (e.clipboardData?.getData('text') || '').trim()
  emit('update:modelValue', paste)
}

onMounted(() => {
  if (props.autoSend) {
    handleSendCode()
  }
})

// 组件销毁时清除定时器
onBeforeUnmount(() => {
  clearInterval(timer)
})

defineExpose({
  startCountDown,
})
</script>

<style lang="less" scoped>
.code-countdown {
  color: var(--neutral-gray-bright);
  user-select: none;
}

.ant-form-item {
  margin-bottom: 0;
}
</style>
